<template>
<div class="revieworder">
    <form @submit.prevent="validateBeforeSubmit">
      <div class="text-xs-left title ma-2">
        Review Your Order
      </div>

      <v-layout justify-center>
        <v-flex xs12 sm10 md8 lg6>
          <BuyerDetailsReview></BuyerDetailsReview>
          <hr />
          <OrderSummary ref="orderSummary"></OrderSummary>

          <div id="recaptcha" class="g-recaptcha" :data-sitekey="recaptchaSiteKey"></div><br />
          <div v-if="showRecaptchMissing" class="error--text subheading">
            You must click the "I am not a robot" checkbox before continuing.
          </div>

          <div class="error--text subheading" v-if="errors.length > 0">
            Please correct errors and re-submit form.

              <div v-for="err in errors" :key="err">
                {{ err }}
              </div>

          </div>
          <div class="error--text" v-if="submissionError">{{ submissionError }}</div>

          <v-btn class="success" :loading=sendingForm :disabled="sendingForm || orderSummaryHasErrors" type="submit">Place Order for {{ totalPrice }}</v-btn>
        </v-flex>
      </v-layout>
    </form>
</div>

</template>

<script>
import {mapState, mapGetters} from 'vuex'
import OrderSummary from './OrderSummary.vue'
import BuyerDetailsReview from './BuyerDetailsReview'

import utils from '../../../utils.ts'
import config from '../../../config.js'

export default {
  name: 'ReviewOrder',
  components: {
    BuyerDetailsReview,
    OrderSummary

  },
  data: function () {
    return {
      anyInvalid: false,
      recaptchaSubmit: false,
      showRecaptchMissing: false,
      sendingForm: false,
      submissionError: null,
      recaptchaSiteKey: config.recaptchaSiteKey,
      errors: []
    }
  },
  mounted: function () {
    grecaptcha.render('recaptcha', { // eslint-disable-line
      sitekey: this.recaptchaSiteKey,
      callback: (token) => {
        console.log('recaptcha callback')
        this.recaptchaSubmit = true
        this.showRecaptchMissing = false
      },
      'expired-callback': () => {
        this.recaptchaSubmit = false
        this.showRecaptchMissing = true
      }
    })
  },
  methods: {
    validateBeforeSubmit: function (event) {
      event.preventDefault()
      this.errors = []

      this.anyInvalid = false
      this.submissionError = false

      // Add the cart info
      const formData = {
        'cartDetails': {
          'cartId': this.cartId,
          'artistId': this.$route.params.aid
        },
        'buyerDetails': {
          'name': this.name,
          'phone': this.phone,
          'email': this.email
        },
        'deliveryDetails': {
          'address': this.deliveryAddress,
          'type': this.deliveryType
        },
        'paymentDetails': {
          'type': this.paymentType
        },
        'noteFromBuyer': this.noteFromBuyer,
        'paymentAmount': {
          'totalPrice' : this.cartTotalPrice,
          'tax' : this.cartSalesTax,
          'shipping' : this.cartShipping

        }
      }

      if (!this.cartId) {
        this.errors.push('Shopping cart information is missing.' + this.cartId)
      }

      // Check Delivery stuff
      if (this.deliveryType !== 'mail' && this.deliveryType !== 'inperson') {
        this.errors.push('Invalid delivery type.')
      }

      // Check payment stuff (and store Stripe token)
      if (this.paymentType === 'creditcard') {
        formData['stripeToken'] = this.stripeToken

        if (this.stripeToken == null || this.stripeToken === undefined) {
          this.errors.push('Missing Stripe token for credit card. Try again.')
        }
      } else if (this.paymentType !== 'inperson') {
        this.errors.push('Invalid payment type.')
      }

      if (!this.recaptchaSubmit) {
        // They didn't use the recaptcha...don't submit.
        this.showRecaptchMissing = true
        // this.errors.push('Missing recaptcha. Click the I\'m not a robot\' box.')
      } else {
        this.showRecaptchMissing = false
        formData['recaptcha'] = grecaptcha.getResponse() // eslint-disable-line
      }

      if (this.locked) {
        // The account is locked, so don't let the order complete.
        this.errors.push("You cannot submit real orders from the demo site.")
      }

      if (this.errors.length === 0 && !this.showRecaptchMissing) {
        // Really submit the form, all looks good!
        this._completeFormSubmission(formData)
      }
      console.log(this.errors)
    },
    _completeFormSubmission: function (formData) {
      // This is called after we have the StripeToken for Credit Cards or
      // immediately for non-credit cards.
      console.log('FORM DATA: ' + JSON.stringify(formData))

      // Change to the sending look
      this.sendingForm = true

      var placeOrder = config.getFunctions().httpsCallable('placeOrder')
      placeOrder(formData).then((result) => {
        // Read result of the Cloud Function.
        console.log('Cloud result: ' + JSON.stringify(result))
        console.log('Cloud result: ' + result.data.text)

        if (result.data.status === 'success') {
          // Save the order confirmation number
          const orderNumber = result.data.orderNumber
          this.$store.commit('shopping/setOrderNumber', {
            orderNumber: orderNumber
          })

          // Clear your cart (locally, it's already cleared remotely)
          this.$store.dispatch('cart/clearCart')

          // Goto Order confirmation page
          this.$router.push({name: 'OrderConfirmation'})
        } else {
          // Show an error
          this.submissionError = 'Error submitting the order. ' + result.data.message

          // reset recaptcha
          grecaptcha.reset() // eslint-disable-line
        }
        this.sendingForm = false
      }).catch((err) => {
        this.submissionError = 'Error submitting the order. ' + err
        this.sendingForm = false
      })
    }
  },
  computed: {
    ...mapState({
      locked: state => state.shopping.public_settings.locked,
      cartId: state => state.cart.cartId,
      deliveryAddress: state => state.cart.deliveryAddress,
      deliveryType: state => state.cart.deliveryType,
      paymentType: state => state.cart.paymentType,
      noteFromBuyer: state => state.cart.noteFromBuyer,
      stripeToken: state => state.cart.stripeToken,
      name: state => state.buyerInfo.name,
      phone: state => state.buyerInfo.phone,
      email: state => state.buyerInfo.email,
      orderSummaryHasErrors: state => state.cart.orderSummaryHasErrors
    }),
    ...mapGetters('cart', {
      cartTotalPrice: 'cartTotalPrice',
      cartShipping: 'cartShipping',
      cartTotalBeforeTax: 'cartTotalBeforeTax',
      cartSalesTax: 'cartSalesTax'
    }),
    totalPrice: function () {
      return utils.formatCurrency(this.cartTotalPrice)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.revieworder {
    width: 100%;
}

.g-recaptcha {
  display: inline-block;
}
</style>
